import type { DragEvent } from 'react'
import type { Company } from '@/constants/companies'
import type { Deal } from '@/constants/crm'

import Avatar from '@/components/ui/avatar'
import Button from '@/components/ui/button'
import CompanyMark from '@/components/generals/company-mark'
import SegmentBar from '@/components/generals/segment-bar'
import { ownerByName } from '@/constants/companies'
import { DEAL_STAGES, stageMeta } from '@/constants/crm'
import { formatMoney } from '@/lib/companies'
import { isSlipping } from '@/lib/crm'
import { formatShortDate } from '@/lib/dates'
import { cn } from '@/lib/utils'
import DotsIcon from '@/assets/images/companies/table/dots-horizontal.svg'
import CalendarIcon from '@/assets/images/_common/calendar.svg'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

interface DealCardProps {
    deal: Deal
    company?: Company
    today: string
    dragging: boolean
    onEdit: () => void
    onOpenCompany: () => void
    onMove: (stage: string) => void
    onDragStart: (event: DragEvent<HTMLElement>) => void
    onDragEnd: () => void
}

export default function DealCard({
    deal,
    company,
    today,
    dragging,
    onEdit,
    onOpenCompany,
    onMove,
    onDragStart,
    onDragEnd
}: DealCardProps) {
    const owner = ownerByName(deal.owner)
    const overdue = isSlipping(deal, today)
    const probability = stageMeta(deal.stage).probability

    return (
        <article
            draggable
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onClick={onEdit}
            data-dragging={dragging}
            className='bg-card ease-power3-out relative flex cursor-grab flex-col gap-3 rounded-lg p-3 shadow-[0px_4px_4px_0px_rgba(15,15,15,0.24),0px_0px_0px_1px_#0e0e0e,inset_0px_1px_0px_0px_rgba(255,255,255,0.06),inset_0px_0px_0px_1px_rgba(255,255,255,0.06)] transition-[background-color,opacity] duration-150 hover:bg-[#21242a] active:cursor-grabbing data-[dragging=true]:opacity-40'
        >
            <div className='flex items-start justify-between gap-2'>
                <span className='caption-style text-soft flex min-w-0 items-center gap-2'>
                    <CompanyMark company={company} className='size-5 rounded-[5px]' />
                    {company ? (
                        <span className='truncate' data-mdlfy-cms={`companies:${company.id}:name`}>
                            {company.name}
                        </span>
                    ) : (
                        <span className='text-subtle truncate'>No company</span>
                    )}
                </span>
                <div onClick={event => event.stopPropagation()} className='-mt-1 -mr-1 shrink-0'>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant='ghost'
                                size='icon-sm'
                                aria-label={`More actions for ${deal.name}`}
                                className='size-6 data-[state=open]:bg-white/6'
                            >
                                <DotsIcon aria-hidden className='size-3' />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                            <DropdownMenuItem onSelect={onEdit}>Edit deal</DropdownMenuItem>
                            {company && <DropdownMenuItem onSelect={onOpenCompany}>Open company</DropdownMenuItem>}
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel>Move to</DropdownMenuLabel>
                            <DropdownMenuRadioGroup value={deal.stage} onValueChange={onMove}>
                                {DEAL_STAGES.map(stage => (
                                    <DropdownMenuRadioItem key={stage.value} value={stage.value}>
                                        {stage.value}
                                    </DropdownMenuRadioItem>
                                ))}
                            </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            <div className='flex flex-col gap-1.5'>
                <h3 className='lead-style font-medium leading-[1.25]'>
                    <button
                        type='button'
                        onClick={event => {
                            event.stopPropagation()
                            onEdit()
                        }}
                        className='focus-visible:ring-ring/60 cursor-pointer rounded-sm text-left outline-none focus-visible:ring-2'
                    >
                        <span data-mdlfy-cms={`deals:${deal.id}:name`}>
                            {deal.name}
                        </span>
                    </button>
                </h3>
                {deal.nextStep && (
                    <p className='caption-style text-subtle line-clamp-2 leading-[1.35]' data-mdlfy-cms={`deals:${deal.id}:next_step`}>
                        {deal.nextStep}
                    </p>
                )}
            </div>

            <div className='flex items-center justify-between gap-2'>
                <span className='lead-style flex items-center gap-1 font-medium tabular-nums'>
                    <span className='text-muted-foreground font-normal'>$</span>
                    <span>{formatMoney(deal.value)}</span>
                </span>
                <span className={cn('caption-style flex items-center gap-1 tabular-nums', overdue ? 'text-danger' : 'text-soft')}>
                    <CalendarIcon aria-hidden className='size-3' />
                    {formatShortDate(deal.closeDate)}
                    {overdue && <span className='sr-only'>, past close date</span>}
                </span>
            </div>

            <div className='flex items-center justify-between gap-2'>
                <span className='caption-style text-soft flex min-w-0 items-center gap-1.5'>
                    <Avatar src={owner.avatar} alt='' className='size-4' />
                    <span className='truncate' data-mdlfy-cms={`deals:${deal.id}:owner`}>
                        {deal.owner}
                    </span>
                </span>
                <SegmentBar percent={probability} segments={10} className='w-[52px]' />
            </div>
        </article>
    )
}
